import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Loader2, Pause, Play, Check, X, Pencil } from 'lucide-react';
import supabase from '../../lib/supabaseClient';
import { card, input, btnPrimary, CATEGORIES, categoryPill, statusPill, relativeTime } from './theme';

/**
 * Keyword Library — fast inline management table.
 * Columns: keyword/phrase, category, status, date added, hit count.
 * Add inline, edit inline, delete. No modals.
 */
export default function KeywordLibrary() {
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // New-row form
  const [newPhrase, setNewPhrase] = useState('');
  const [newCategory, setNewCategory] = useState(CATEGORIES[0]);

  // Edit-row buffer
  const [editPhrase, setEditPhrase] = useState('');
  const [editCategory, setEditCategory] = useState(CATEGORIES[0]);

  const fetchKeywords = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('hunter_keywords')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setKeywords(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchKeywords();
  }, [fetchKeywords]);

  const addKeyword = async () => {
    const phrase = newPhrase.trim();
    if (!phrase) return;
    setSaving(true);
    const { data, error } = await supabase
      .from('hunter_keywords')
      .insert({ phrase, category: newCategory })
      .select()
      .single();
    if (!error && data) {
      setKeywords((prev) => [data, ...prev]);
      setNewPhrase('');
      setNewCategory(CATEGORIES[0]);
    }
    setSaving(false);
  };

  const toggleStatus = async (kw) => {
    setBusyId(kw.id);
    const next = kw.status === 'active' ? 'paused' : 'active';
    const { error } = await supabase.from('hunter_keywords').update({ status: next }).eq('id', kw.id);
    if (!error) setKeywords((prev) => prev.map((k) => (k.id === kw.id ? { ...k, status: next } : k)));
    setBusyId(null);
  };

  const deleteKeyword = async (id) => {
    setBusyId(id);
    const { error } = await supabase.from('hunter_keywords').delete().eq('id', id);
    if (!error) setKeywords((prev) => prev.filter((k) => k.id !== id));
    setBusyId(null);
  };

  const startEdit = (kw) => {
    setEditingId(kw.id);
    setEditPhrase(kw.phrase);
    setEditCategory(kw.category);
  };

  const saveEdit = async (id) => {
    const phrase = editPhrase.trim();
    if (!phrase) return;
    setBusyId(id);
    const { error } = await supabase
      .from('hunter_keywords')
      .update({ phrase, category: editCategory })
      .eq('id', id);
    if (!error) {
      setKeywords((prev) =>
        prev.map((k) => (k.id === id ? { ...k, phrase, category: editCategory } : k))
      );
      setEditingId(null);
    }
    setBusyId(null);
  };

  return (
    <div className={`${card} shadow-sm overflow-hidden`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
            <th className="px-4 py-3">Keyword / Phrase</th>
            <th className="px-4 py-3 w-40">Category</th>
            <th className="px-4 py-3 w-28">Status</th>
            <th className="px-4 py-3 w-32">Added</th>
            <th className="px-4 py-3 w-20 text-right">Hits</th>
            <th className="px-4 py-3 w-28 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {/* Inline add row */}
          <tr className="bg-gray-50/50">
            <td className="px-4 py-3">
              <input
                className={`${input} w-full`}
                placeholder="Add a keyword or phrase…"
                value={newPhrase}
                onChange={(e) => setNewPhrase(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addKeyword()}
              />
            </td>
            <td className="px-4 py-3">
              <select
                className={`${input} w-full`}
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </td>
            <td className="px-4 py-3 text-gray-400">—</td>
            <td className="px-4 py-3 text-gray-400">—</td>
            <td className="px-4 py-3 text-right text-gray-400">—</td>
            <td className="px-4 py-3 text-right">
              <button className={btnPrimary} disabled={saving || !newPhrase.trim()} onClick={addKeyword}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add
              </button>
            </td>
          </tr>

          {/* Existing rows */}
          {loading ? (
            <tr>
              <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin inline" />
              </td>
            </tr>
          ) : keywords.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                No keywords yet. Add your first above.
              </td>
            </tr>
          ) : (
            keywords.map((kw) => {
              const editing = editingId === kw.id;
              const busy = busyId === kw.id;
              return (
                <tr key={kw.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    {editing ? (
                      <input
                        className={`${input} w-full`}
                        value={editPhrase}
                        onChange={(e) => setEditPhrase(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && saveEdit(kw.id)}
                        autoFocus
                      />
                    ) : (
                      <span className="text-gray-800">{kw.phrase}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {editing ? (
                      <select
                        className={`${input} w-full`}
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                      >
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    ) : (
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${categoryPill[kw.category] || ''}`}>
                        {kw.category}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full capitalize ${statusPill[kw.status] || ''}`}>
                      {kw.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{relativeTime(kw.created_at)}</td>
                  <td className="px-4 py-3 text-right text-gray-700 tabular-nums">{kw.hit_count ?? 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {editing ? (
                        <>
                          <IconBtn title="Save" onClick={() => saveEdit(kw.id)} busy={busy}>
                            <Check className="w-4 h-4 text-green-600" />
                          </IconBtn>
                          <IconBtn title="Cancel" onClick={() => setEditingId(null)}>
                            <X className="w-4 h-4 text-gray-400" />
                          </IconBtn>
                        </>
                      ) : (
                        <>
                          <IconBtn title="Edit" onClick={() => startEdit(kw)}>
                            <Pencil className="w-4 h-4 text-gray-400" />
                          </IconBtn>
                          <IconBtn
                            title={kw.status === 'active' ? 'Pause' : 'Activate'}
                            onClick={() => toggleStatus(kw)}
                            busy={busy}
                          >
                            {kw.status === 'active' ? (
                              <Pause className="w-4 h-4 text-amber-500" />
                            ) : (
                              <Play className="w-4 h-4 text-green-600" />
                            )}
                          </IconBtn>
                          <IconBtn title="Delete" onClick={() => deleteKeyword(kw.id)} busy={busy}>
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </IconBtn>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function IconBtn({ children, title, onClick, busy }) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={busy}
      className="p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : children}
    </button>
  );
}
